'use client'

import {
  Shield,
  ShieldOff,
  Bot,
  Search,
  Eye,
  Link2,
  Database,
  ArrowRight,
  CheckCircle2,
  XCircle,
} from 'lucide-react'

const brandColors = {
  primary: '#008dcc',
  accent: '#FDC304',
  pink: '#F90B8A',
  purple: '#8b5cf6',
  green: '#10b981',
  red: '#ef4444',
}

export function BotCategoryDiagram() {
  const categories = [
    {
      name: 'Training Bots',
      action: 'BLOCKED',
      color: brandColors.red,
      icon: ShieldOff,
      description:
        'Collect content for model weights. No attribution, no traffic back.',
      bots: [
        'Google-Extended',
        'CCBot',
        'anthropic-ai',
        'cohere-ai',
        'Meta-ExternalAgent',
        'Bytespider',
        'Applebot-Extended',
      ],
    },
    {
      name: 'Search Bots',
      action: 'ALLOWED',
      color: brandColors.green,
      icon: Search,
      description:
        'Power AI search results. Cite with links. Drive referral traffic.',
      bots: [
        'OAI-SearchBot',
        'Claude-SearchBot',
        'PerplexityBot',
        'GoogleOther',
      ],
    },
    {
      name: 'User Bots',
      action: 'ALLOWED',
      color: brandColors.primary,
      icon: Eye,
      description:
        'Real-time fetches when a user asks a question. Always cite the source.',
      bots: ['ChatGPT-User', 'Claude-User'],
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          The Three Types of AI Bots
        </h3>
        <div className='space-y-4'>
          {categories.map(cat => (
            <div
              key={cat.name}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <div className='flex items-start gap-3 mb-3'>
                <div
                  className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                  style={{
                    backgroundColor: `${cat.color}15`,
                    color: cat.color,
                  }}
                >
                  <cat.icon className='w-5 h-5' />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='text-sm font-semibold text-foreground'>
                      {cat.name}
                    </span>
                    <span
                      className='text-xs font-mono px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${cat.color}15`,
                        color: cat.color,
                      }}
                    >
                      {cat.action}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {cat.description}
                  </p>
                </div>
              </div>
              <div className='flex flex-wrap gap-1.5 mt-2'>
                {cat.bots.map(bot => (
                  <span
                    key={bot}
                    className='text-xs font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground'
                  >
                    {bot}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function CrawlToClickDiagram() {
  const data = [
    { name: 'Google', ratio: '14:1', bar: 5, color: brandColors.green },
    { name: 'Perplexity', ratio: '194:1', bar: 30, color: brandColors.primary },
    { name: 'OpenAI', ratio: '1,700:1', bar: 65, color: brandColors.accent },
    { name: 'Anthropic', ratio: '38,000:1', bar: 95, color: brandColors.red },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          Crawl-to-Click Ratio by Provider
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Crawls per referral visit (Cloudflare, July 2025)
        </p>
        <div className='space-y-3'>
          {data.map(d => (
            <div key={d.name} className='space-y-1'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium text-foreground'>
                  {d.name}
                </span>
                <span className='text-xs font-mono' style={{ color: d.color }}>
                  {d.ratio}
                </span>
              </div>
              <div className='h-2 bg-muted rounded-full overflow-hidden'>
                <div
                  className='h-full rounded-full'
                  style={{
                    width: `${d.bar}%`,
                    backgroundColor: d.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className='text-xs text-muted-foreground mt-4 text-center'>
          Training bots consume the most resources while returning the least
          traffic
        </p>
      </div>
    </div>
  )
}

export function SegmentationFlowDiagram() {
  const steps = [
    {
      icon: Bot,
      label: 'AI bot hits your site',
      detail: 'Checks robots.txt first',
      color: brandColors.purple,
    },
    {
      icon: Shield,
      label: 'robots.txt identifies bot type',
      detail: 'Training vs. search vs. user',
      color: brandColors.primary,
    },
    {
      icon: Database,
      label: 'Training bots get blocked',
      detail: 'Disallow: / (except llms.txt)',
      color: brandColors.red,
    },
    {
      icon: Link2,
      label: 'Search bots get full access',
      detail: 'Crawl, cite, link back to you',
      color: brandColors.green,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          How Bot Segmentation Works
        </h3>
        <div className='space-y-4'>
          {steps.map((step, i) => (
            <div key={step.label}>
              <div className='flex items-start gap-4'>
                <div
                  className='flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center'
                  style={{
                    backgroundColor: `${step.color}15`,
                    color: step.color,
                  }}
                >
                  <step.icon className='w-6 h-6' />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span
                      className='text-xs font-mono px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${step.color}15`,
                        color: step.color,
                      }}
                    >
                      Step {i + 1}
                    </span>
                  </div>
                  <p className='text-foreground font-medium mt-1'>
                    {step.label}
                  </p>
                  <p className='text-sm text-muted-foreground'>{step.detail}</p>
                </div>
              </div>
              {i < steps.length - 1 && (
                <div className='flex justify-center py-2'>
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function BlockVsAllowOutcomeDiagram() {
  const rows = [
    {
      label: 'What happens to your content',
      blocked: 'Absorbed into model weights, no trace',
      allowed: 'Cited with link back to your site',
    },
    {
      label: 'Traffic you receive',
      blocked: 'Zero referrals',
      allowed: 'Direct referral traffic from AI answers',
    },
    {
      label: 'Attribution',
      blocked: 'None, content is anonymous training data',
      allowed: 'Source URL in every AI search result',
    },
    {
      label: 'Control over your content',
      blocked: 'None after crawl, baked into weights',
      allowed: 'Update content, citations update too',
    },
    {
      label: 'Server resource cost',
      blocked: 'High (aggressive bulk crawling)',
      allowed: 'Low (on-demand fetches per query)',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          Training Bots vs. Search Bots: What You Get
        </h3>
        <div className='space-y-3'>
          {rows.map(row => (
            <div
              key={row.label}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <p className='text-sm font-medium text-foreground mb-3'>
                {row.label}
              </p>
              <div className='grid grid-cols-2 gap-3'>
                <div
                  className='rounded-lg px-3 py-2'
                  style={{
                    backgroundColor: `${brandColors.red}08`,
                    borderLeft: `3px solid ${brandColors.red}40`,
                  }}
                >
                  <div className='flex items-center gap-1.5 mb-1'>
                    <XCircle
                      className='w-3.5 h-3.5'
                      style={{ color: brandColors.red }}
                    />
                    <span
                      className='text-xs font-medium'
                      style={{ color: brandColors.red }}
                    >
                      Training
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground'>{row.blocked}</p>
                </div>
                <div
                  className='rounded-lg px-3 py-2'
                  style={{
                    backgroundColor: `${brandColors.green}08`,
                    borderLeft: `3px solid ${brandColors.green}40`,
                  }}
                >
                  <div className='flex items-center gap-1.5 mb-1'>
                    <CheckCircle2
                      className='w-3.5 h-3.5'
                      style={{ color: brandColors.green }}
                    />
                    <span
                      className='text-xs font-medium'
                      style={{ color: brandColors.green }}
                    >
                      Search
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground'>{row.allowed}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
